import axios from 'axios';
import { getProductInstanceId } from '../helpers/cookieHelper';

const ORIGIN_APPLICATION_NAME = 'WEBSITE_BUILDER';

class ImageLibraryError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = 'ImageLibraryError';
    this.status = status;
  }
}

const getImageLibraryUrl = () =>
  (process.env.REACT_APP_IMAGE_LIBRARY_URL || '').replace(/\/+$/, '');

const normalizeUploadError = (error) => {
  if (error instanceof ImageLibraryError) return error;
  if (error?.response) {
    return new ImageLibraryError(
      error.response.data?.message ||
        error.response.data?.error ||
        'Could not upload the image.',
      error.response.status
    );
  }
  if (error?.request) {
    return new ImageLibraryError(
      'Network error while uploading the image. Please check your connection.',
      0
    );
  }
  return new ImageLibraryError(
    error?.message || 'Could not upload the image.',
    0
  );
};

export const uploadImageToImageLibrary = async (file, onUploadProgress) => {
  const imageLibraryUrl = getImageLibraryUrl();
  const sfamToken = process.env.REACT_APP_SFAM_TOKEN;
  const authToken = localStorage.getItem('authToken');
  const productInstanceId = getProductInstanceId();

  if (!imageLibraryUrl) {
    throw new ImageLibraryError(
      'Image library is not configured (missing REACT_APP_IMAGE_LIBRARY_URL).'
    );
  }
  if (!sfamToken) {
    throw new ImageLibraryError(
      'Image library authentication is not configured (missing REACT_APP_SFAM_TOKEN).'
    );
  }
  if (!authToken) {
    throw new ImageLibraryError('Authentication token is not available. Please refresh and try again.');
  }
  if (!productInstanceId) {
    throw new ImageLibraryError('Product instance ID is not available. Please refresh and try again.');
  }
  if (!file) {
    throw new ImageLibraryError('No file selected.');
  }

  const formData = new FormData();
  formData.append('files', file);
  formData.append('originApplicationName', ORIGIN_APPLICATION_NAME);

  try {
    const response = await axios.post(`${imageLibraryUrl}/v1/images`, formData, {
      headers: {
        Authorization: `Basic ${sfamToken}`,
        Token: authToken,
      },
      params: { productInstanceId },
      onUploadProgress: (progressEvent) => {
        const total = progressEvent.total;
        if (typeof onUploadProgress === 'function' && total) {
          onUploadProgress(Math.round((progressEvent.loaded * 100) / total));
        }
      },
    });

    const uploadedImage = Array.isArray(response.data)
      ? response.data.find((image) => {
          const imageUrl = image?.imageUri || image?.public_url;
          const successful = !image?.status || image.status === 'SUCCESS';
          return successful && typeof imageUrl === 'string' && imageUrl.length > 0;
        })
      : null;
    const uploadedImageUrl = uploadedImage?.imageUri || uploadedImage?.public_url;

    if (!uploadedImageUrl) {
      throw new ImageLibraryError(
        'Upload finished but the image URL was not returned. Please try again.'
      );
    }

    if (typeof onUploadProgress === 'function') {
      onUploadProgress(100);
    }

    return {
      success: true,
      data: {
        url: uploadedImageUrl,
        image_id: uploadedImage.imageId ?? uploadedImage.image_id,
      },
    };
  } catch (error) {
    throw normalizeUploadError(error);
  }
};
